import { useTranslation } from 'react-i18next'
import { ChevronDown, Ruler, Building2, Factory, Home as HomeIcon, Store, Wrench, ArrowRight } from 'lucide-react'
import { LocalizedLink } from '@/components/LocalizedLink'
import { StructuredData } from '@/components/StructuredData'
import { usePageTitle } from '@/hooks/usePageTitle'
import { CtaBanner, ProjectCard, ScrollReveal, SectionHeading, ServiceCard } from '@/components/ui'

const projectImages = [
  { src: '/media/images/interni-card.jpg', title: 'Ristrutturazione appartamento', category: 'Interni' },
  { src: '/media/images/2.jpg', title: 'Appartamento Crema', category: 'Ristrutturazione' },
  { src: '/media/images/esterni-card.jpg', title: 'Riqualificazione esterna', category: 'Esterni' },
  { src: '/media/images/home2.jpg', title: 'Lavori chiavi in mano', category: 'Chiavi in mano' },
]

const areaIcons = {
  design: <Ruler className="h-7 w-7" />,
  civil: <Building2 className="h-7 w-7" />,
  industrial: <Factory className="h-7 w-7" />,
  commercial: <Store className="h-7 w-7" />,
  renovation: <HomeIcon className="h-7 w-7" />,
  maintenance: <Wrench className="h-7 w-7" />,
}

const areaKeys = Object.keys(areaIcons) as Array<keyof typeof areaIcons>

export default function Home() {
  const { t } = useTranslation()
  usePageTitle('home.meta.title', 'home.meta.description')

  const scrollToHistory = () => {
    document.getElementById('history')?.scrollIntoView({ behavior: 'smooth' })
  }

  return (
    <>
      <StructuredData />
      <main id="main-content">
        {/* Hero — full-bleed, no text, image-only lock-up */}
        <section className="relative h-screen w-full overflow-hidden" aria-label={t('home.hero.label')}>
          <picture>
            <source media="(min-width: 768px)" srcSet="/media/images/hero-desktop.png" />
            <img
              src="/media/images/hero-mobile.png"
              alt={t('home.hero.imageAlt')}
              className="h-full w-full object-cover"
              fetchPriority="high"
            />
          </picture>

          <button
            onClick={scrollToHistory}
            className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/80 hover:text-white transition-colors"
            aria-label={t('aria.scrollDown')}
          >
            <ChevronDown className="h-8 w-8 animate-bounce" />
          </button>
        </section>

        {/* History */}
        <section id="history" className="relative bg-surface py-16 md:py-24 overflow-hidden">
          <div className="absolute inset-0 pointer-events-none select-none" aria-hidden="true">
            <span className="absolute top-1/2 left-0 -translate-y-1/2 -translate-x-1/4 md:translate-x-0 font-display text-[8rem] md:text-[12rem] lg:text-[16rem] font-bold text-heading/[0.035] leading-none">
              1977
            </span>
          </div>

          <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <ScrollReveal className="order-2 lg:order-1">
                <div className="max-w-xl">
                  <h2 className="font-display text-h2 text-heading mb-6">{t('home.history.title')}</h2>
                  <div className="space-y-4 text-text leading-relaxed">
                    <p>{t('home.history.paragraph1')}</p>
                    <p>{t('home.history.paragraph2')}</p>
                  </div>
                </div>
              </ScrollReveal>
              <ScrollReveal className="order-1 lg:order-2" delay={0.15}>
                <figure className="relative">
                  <div className="absolute -top-3 -left-3 w-24 h-24 border-l-4 border-t-4 border-brand rounded-tl-xl" aria-hidden="true" />
                  <img
                    src="/media/images/home2.jpg"
                    alt={t('home.history.imageAlt')}
                    loading="lazy"
                    className="relative w-full rounded-xl shadow-lg object-cover aspect-[4/3]"
                  />
                  <div className="absolute -bottom-3 -right-3 w-24 h-24 border-r-4 border-b-4 border-brand rounded-br-xl" aria-hidden="true" />
                </figure>
              </ScrollReveal>
            </div>
          </div>
        </section>

        {/* Services — macroaree */}
        <section id="intro" className="bg-bg py-16 md:py-24">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <ScrollReveal className="mb-12 md:mb-16">
              <SectionHeading
                as="h1"
                title={t('servicesPage.overview.title')}
                subtitle={t('servicesPage.overview.subtitle')}
                align="center"
              />
            </ScrollReveal>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {areaKeys.map((key, index) => (
                <ScrollReveal key={key} delay={index * 0.1}>
                  <ServiceCard
                    icon={areaIcons[key]}
                    title={t(`servicesPage.areas.${key}.title`)}
                    description={t(`servicesPage.areas.${key}.description`)}
                  />
                </ScrollReveal>
              ))}
            </div>
          </div>
        </section>

        {/* Projects band */}
        <section className="bg-heading py-16 md:py-24">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <ScrollReveal className="mb-10 md:mb-12">
              <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
                <div className="max-w-2xl">
                  <h2 className="font-display text-h2 text-white mb-3">{t('home.projects.title')}</h2>
                  <p className="text-white/70 text-lg">{t('home.projects.subtitle')}</p>
                </div>
                <LocalizedLink
                  to="projects"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-brand text-white font-semibold rounded-lg hover:bg-brand-light transition-colors self-start md:self-auto"
                >
                  {t('buttons.viewProjects')}
                </LocalizedLink>
              </div>
            </ScrollReveal>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {projectImages.map((project, index) => (
                <ScrollReveal key={project.src} delay={index * 0.1}>
                  <ProjectCard
                    image={project.src}
                    title={project.title}
                    category={project.category}
                  />
                </ScrollReveal>
              ))}
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <CtaBanner
          title={t('home.cta.title')}
          action={
            <LocalizedLink
              to="contacts"
              className="inline-flex items-center gap-2 px-8 py-4 bg-white text-heading font-semibold rounded-lg hover:bg-bg transition-colors shadow-lg"
            >
              {t('nav.contacts')}
              <ArrowRight className="h-5 w-5" />
            </LocalizedLink>
          }
        />
      </main>
    </>
  )
}
